import React from 'react';
import { useCart } from '../../../context/CartContext';
import { useWishlist } from '../../../context/WishlistContext';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { useStorefrontProducts } from './useStorefrontProducts';

// Reusable homepage product row. Fetches the merchant catalog once (shared,
// module-cached) and renders a titled 4-up grid from a `select` function.
// Renders NOTHING when the selection is empty, so a row never shows blank.
const ProductCollection = ({
  eyebrow,
  title,
  subtitle,
  viewAllPath = '/product-catalog',
  select,
  badge,          // fixed label, e.g. 'New' — falls back to the sale/featured heuristic
  max = 4,
  altBg = false,  // subtle alternating section background
}) => {
  const navigate = useNavigate();
  const cart = useCart();
  const wishlist = useWishlist();
  const { products: all } = useStorefrontProducts();

  const picked = (typeof select === 'function' ? select(all) : all).slice(0, max);
  const items = picked.map((p) => ({
    id: p.id,
    name: p.name,
    brand: 'Bounderlaster',
    price: (p.sale_price_cents ?? p.price_cents) / 100,
    originalPrice: p.sale_price_cents != null ? p.price_cents / 100 : null,
    rating: p.rating_avg ?? 0,
    reviews: p.review_count ?? 0,
    image: p.image_url,
    alt: p.name,
    badge: badge ?? (p.is_featured ? 'Bestseller' : (p.sale_price_cents != null ? 'Sale' : null)),
  }));

  // Never render an empty row.
  if (items.length === 0) return null;

  const handleProductClick = (product) => navigate(`/product-details?id=${product.id}`);
  const handleViewAll = () => navigate(viewAllPath);

  return (
    <section className={`py-20 px-4 sm:px-6 lg:px-8 ${altBg ? 'bg-muted/30' : ''}`}>
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-12">
          <div>
            {eyebrow && (
              <motion.p
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="text-sm font-semibold uppercase tracking-wide text-primary mb-2">
                {eyebrow}
              </motion.p>
            )}
            <motion.h2
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="text-4xl md:text-5xl font-heading font-bold text-foreground mb-2">
              {title}
            </motion.h2>
            {subtitle && (
              <motion.p
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="text-lg text-muted-foreground">
                {subtitle}
              </motion.p>
            )}
          </div>
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}>
            <Button
              onClick={handleViewAll}
              variant="outline"
              className="hidden md:flex items-center gap-2">
              View All
              <Icon name="ArrowRight" size={16} />
            </Button>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {items.map((product, index) => (
            <motion.div
              key={product?.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -8 }}
              onClick={() => handleProductClick(product)}
              className="cursor-pointer group">
              <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300">
                <div className="relative overflow-hidden aspect-square">
                  <img
                    src={product?.image}
                    alt={product?.alt}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                  {product?.badge && (
                    <div className="absolute top-3 left-3">
                      <span className="bg-primary text-primary-foreground text-xs font-semibold px-3 py-1 rounded-full">
                        {product?.badge}
                      </span>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); wishlist.toggle(product); }}
                    aria-label={wishlist.has(product?.id) ? 'Remove from wishlist' : 'Add to wishlist'}
                    className="absolute top-3 right-3 bg-background/80 backdrop-blur-sm rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-background">
                    <Icon name="Heart" size={20} className={wishlist.has(product?.id) ? 'text-error fill-current' : 'text-foreground'} />
                  </button>
                </div>
                <div className="p-5">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                    {product?.brand}
                  </p>
                  <h3 className="text-lg font-semibold text-foreground mb-2 line-clamp-1">
                    {product?.name}
                  </h3>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="flex items-center gap-1">
                      <Icon name="Star" size={16} className="text-amber-500 fill-current" />
                      <span className="text-sm font-medium">{product?.rating}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      ({product?.reviews})
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-bold text-foreground">
                        ${product?.price}
                      </span>
                      {product?.originalPrice != null && (
                        <span className="text-sm text-muted-foreground line-through">
                          ${product?.originalPrice}
                        </span>
                      )}
                    </div>
                    <Button
                      size="sm"
                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={(e) => { e.stopPropagation(); cart.addItem(product, 1); }}>
                      Add to Cart
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mt-8 text-center md:hidden">
          <Button
            onClick={handleViewAll}
            variant="outline"
            className="w-full sm:w-auto">
            View All
            <Icon name="ArrowRight" size={16} className="ml-2" />
          </Button>
        </motion.div>
      </div>
    </section>
  );
};

export default ProductCollection;
